'use client';

import { useState } from 'react';
import { useItems } from '@/hooks/useItems';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import type { Item } from '@/lib/schemas';
import type { ItemSelectionResult } from '@/types/purchase-order-form';
import { Search, Package, AlertCircle } from 'lucide-react';

interface ItemSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (item: ItemSelectionResult) => void;
  hospitalId: string;
}

export function ItemSelector({ open, onOpenChange, onSelect, hospitalId }: ItemSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [quantity, setQuantity] = useState<number>(1);

  const { data: itemsResponse = { data: [], totalCount: 0 }, isLoading } = useItems({ searchQuery });
  const items = itemsResponse.data;

  const columns = [
    {
      key: 'code',
      header: 'Code',
      render: (item: Item) => (
        <span className="font-mono text-sm text-gray-600">{item.code}</span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (item: Item) => (
        <span className="font-medium">{item.name}</span>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (item: Item) => (
        <Badge variant="outline">{item.category}</Badge>
      ),
    },
    {
      key: 'unit_of_measure',
      header: 'UOM',
      render: (item: Item) => (
        <span className="text-sm text-gray-600">{item.unit_of_measure}</span>
      ),
    },
    {
      key: 'quantity',
      header: 'In Stock',
      render: (item: Item) => (
        <span className={`font-mono ${item.quantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {item.quantity}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (item: Item) => (
        <Button
          size="sm"
          variant={selectedItem?.item_id === item.item_id ? 'secondary' : 'outline'}
          onClick={() => setSelectedItem(item)}
          disabled={item.quantity === 0}
        >
          {item.quantity === 0 ? 'Out of Stock' : 'Select'}
        </Button>
      ),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Select Item</DialogTitle>
          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by code, name, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <DataTable
            columns={columns}
            data={items}
            loading={isLoading}
            getItemId={(item) => item.item_id!.toString()}
          />
        </div>

        <div className="border-t p-4 bg-gray-50">
          {selectedItem ? (
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-lg">{selectedItem.name}</h4>
                <p className="text-sm text-gray-600">{selectedItem.code} • {selectedItem.unit_of_measure}</p>
                <p className="text-sm text-gray-500 mt-1">{selectedItem.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Quantity</label>
                  <Input
                    type="number"
                    min={1}
                    max={selectedItem.quantity}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Available: {selectedItem.quantity}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Unit Price</label>
                  <div className="flex items-center border rounded-md px-3 h-10 bg-white">
                    <span className="text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={selectedItem.unit_cost || 0}
                      onChange={() => {}}
                      className="flex-1 outline-none ml-1"
                    />
                  </div>
                </div>
              </div>
              {selectedItem && quantity > selectedItem.quantity && (
                <div className="flex items-center gap-2 text-amber-600 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  <span>Requested quantity exceeds available stock</span>
                </div>
              )}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span>Line total:</span>
                  <span className="font-mono font-semibold text-lg">
                    ${(quantity * (selectedItem.unit_cost || 0)).toFixed(2)}
                  </span>
                </div>
                <Button
                  className="w-full"
                  onClick={() => {
                    onSelect({
                      item_id: selectedItem.item_id!.toString(),
                      item_code: selectedItem.code,
                      item_name: selectedItem.name,
                      quantity_ordered: quantity,
                      unit_price: selectedItem.unit_cost || 0,
                      unit_of_measure: selectedItem.unit_of_measure,
                      line_total: quantity * (selectedItem.unit_cost || 0),
                      available_quantity: selectedItem.quantity,
                    });
                    setSelectedItem(null);
                    setQuantity(1);
                  }}
                >
                  Add to PO
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Select an item to add to the purchase order</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
